import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { passPublic, rememberAffiliate } from '../../services/passApi';
import { Sparkles, ArrowRight, AlertCircle, BookOpen, MapPin, Gift } from 'lucide-react';

export default function AffiliateProfile() {
  const { affiliateSlug } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    passPublic
      .affiliate(affiliateSlug)
      .then((res) => {
        if (!alive) return;
        setData(res.data);
        rememberAffiliate(res.data.affiliate.slug, res.data.affiliate.affiliate_code);
      })
      .catch((err) => alive && setError(err.response?.data?.detail || 'No encontrado'))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [affiliateSlug]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Cargando...</div>;
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="text-center">
          <AlertCircle className="w-10 h-10 mx-auto text-orange-500 mb-3" />
          <p className="text-slate-600">{error}</p>
          <Link to="/pass" className="text-teal-600 underline mt-3 inline-block">Volver</Link>
        </div>
      </div>
    );
  }

  const { affiliate, offers, guides = [] } = data;
  const city = affiliate.city || 'Vallarta';

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-900 text-white py-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          {affiliate.avatar_url ? (
            <img
              src={affiliate.avatar_url}
              alt={affiliate.display_name}
              className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover mx-auto mb-4 border-4 border-teal-500 shadow-lg"
            />
          ) : (
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-slate-800 border-4 border-teal-500 flex items-center justify-center text-teal-300 mx-auto mb-4">
              <Sparkles className="w-10 h-10" />
            </div>
          )}
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/20 text-teal-300 text-xs font-semibold mb-3">
            <Sparkles className="w-3.5 h-3.5" /> Recomendado por
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-2">{affiliate.display_name}</h1>
          {affiliate.bio && (
            <p className="text-slate-300 max-w-2xl mx-auto italic leading-relaxed">
              "{affiliate.bio}"
            </p>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10 space-y-10">
        {/* Guides band — appears above offers when there are any */}
        {guides.length > 0 && (
          <section>
            <div className="bg-gradient-to-r from-teal-50 to-slate-50 border border-teal-200 rounded-2xl p-5 mb-4">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-teal-600 text-white text-[11px] font-semibold mb-2">
                <BookOpen className="w-3 h-3" /> Cosas para hacer en {city}
              </div>
              <h2 className="text-xl font-bold text-slate-900">Recomendaciones locales de {affiliate.display_name}</h2>
              <p className="text-sm text-slate-600">Playas, miradores, mercados y experiencias que te van a encantar.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {guides.map((g) => <GuideCard key={g.id} guide={g} />)}
            </div>
          </section>
        )}

        {/* Offers */}
        <section>
          {guides.length > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-2xl p-5 mb-4">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-orange-500 text-white text-[11px] font-semibold mb-2">
                <Gift className="w-3 h-3" /> Promos y experiencias
              </div>
              <h2 className="text-xl font-bold text-slate-900">Ofertas seleccionadas</h2>
            </div>
          )}
          {offers.length === 0 ? (
            <p className="text-slate-500 text-center py-10">
              Este afiliado aún no tiene ofertas activas. Vuelve pronto.
            </p>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {offers.map((o) => (
                <OfferCard key={o.id} offer={o} affiliateCode={affiliate.affiliate_code} />
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function GuideCard({ guide }) {
  return (
    <Link
      to={`/pass/guia/${guide.slug}`}
      className="block bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition"
    >
      <div className="aspect-[16/9] bg-slate-100 relative">
        {guide.cover_image_url ? (
          <img src={guide.cover_image_url} alt={guide.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-teal-400">
            <BookOpen className="w-10 h-10" />
          </div>
        )}
        <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-teal-600 text-white text-[10px] font-bold uppercase tracking-wide">
          {guide.category}
        </span>
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-slate-900 leading-tight">{guide.title}</h3>
        {guide.summary && <p className="text-sm text-slate-600 mt-1 line-clamp-2">{guide.summary}</p>}
        <div className="flex items-center justify-between mt-3 text-xs">
          <span className="text-slate-500 inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {guide.city}</span>
          <span className="text-teal-600 inline-flex items-center gap-1 font-semibold">
            Leer más <ArrowRight className="w-3 h-3" />
          </span>
        </div>
      </div>
    </Link>
  );
}

function OfferCard({ offer, affiliateCode }) {
  const benefit = offer.customer_benefits?.[0]?.name;
  const cover = offer.images?.[0];
  return (
    <Link
      to={`/pass/oferta/${offer.slug}?ref=${affiliateCode}`}
      className="block bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition"
    >
      <div className="aspect-[16/10] bg-slate-100 relative">
        {cover ? (
          <img src={cover} alt={offer.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-300">
            <Gift className="w-10 h-10" />
          </div>
        )}
        <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-orange-500 text-white text-[10px] font-bold uppercase tracking-wide">
          {offer.category}
        </span>
      </div>
      <div className="p-5">
        <h3 className="font-semibold text-slate-900 mb-1 leading-tight">{offer.title}</h3>
        {benefit && <p className="text-sm text-slate-600 mb-3 line-clamp-2">{benefit}</p>}
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-500">
            {offer.mode === 'free_claim' ? 'Promo gratis' : offer.mode}
          </span>
          <span className="text-orange-500 inline-flex items-center gap-1 font-semibold">
            Ver detalles <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </div>
    </Link>
  );
}
